// Meta's `platform` — which surface a lead came from. Pure.
//
// ⚠️⚠️ ONE TABLE. The Rawdata parse stores the CODE (`fb` | `ig` | null), and
// every reading of it goes through these rows: the Lead Log's label AND the
// Anytime Fitness `origin_source`. Two tables would let the screen say
// "Facebook" while the dealer's CRM was told something else.
//
// ⚠️ AF's origin_source is FREE TEXT that AF stores verbatim and validates not
// at all (Addendum 4), so a typo here lands in a club CRM silently and
// permanently. Each value was proven against the live endpoint (Facebook
// ROX24495, Instagram ROX24498, Meta ROX24496); platform.test.ts pins every
// literal, and a source guard keeps them out of anytimefitness.ts.
//
// ⚠️ NEVER A GUESS: anything that is not exactly `fb` or `ig` after trimming
// and lowercasing is null — not Facebook. Meta makes no claim about the
// surface for a lead no ad served (the platform is empty there), and "Meta" is
// the honest answer to AF in that case.

import type { LeadPlatform } from '../../types/leadRelay.js';

export type { LeadPlatform };

export const LEAD_PLATFORM_ROWS = Object.freeze([
  Object.freeze({ code: 'fb', label: 'Facebook', afOriginSource: 'Facebook' }),
  Object.freeze({ code: 'ig', label: 'Instagram', afOriginSource: 'Instagram' }),
  Object.freeze({ code: null, label: 'Unknown', afOriginSource: 'Meta' }),
] as const);

export type AfOriginSource = (typeof LEAD_PLATFORM_ROWS)[number]['afOriginSource'];

/** Rawdata's `platform` → the stored code. Trim, lowercase, then exactly fb/ig — else null. */
export function parseLeadPlatform(value: unknown): LeadPlatform | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  return v === 'fb' || v === 'ig' ? v : null;
}

function rowFor(code: LeadPlatform | null | undefined) {
  return LEAD_PLATFORM_ROWS.find((row) => row.code === (code ?? null))!;
}

/** fb → Facebook, ig → Instagram, null → Unknown. */
export function platformLabel(code: LeadPlatform | null | undefined): string {
  return rowFor(code).label;
}

/** fb → Facebook, ig → Instagram, null → Meta. What Anytime Fitness is sent. */
export function afOriginSourceFor(code: LeadPlatform | null | undefined): AfOriginSource {
  return rowFor(code).afOriginSource;
}
